/** @module SiteAdmin/HomePane/NewPaneForm */
define([
	"dojo/_base/declare",
	"dojo/_base/lang",
	"dojo/promise/all",
	"dojo/store/Memory",
	"csi",
	"csi/_base/underscore",
	"csi/def/Relationship",
	"csi/html",
	"csi/locale",
	"csi/form/Widget",
	"csi/store/_base",
	"csi/xml",
	"csi/xml/Query",
	"./util"
], function(declare, lang, all, Memory, csi, _, Relationship, html, locale, FormWidget, csiStore, xml, Query, HomePageUtil) {

	var CONTEXT = 'DashboardPaneContext';
	var CONTEXT_TYPE = 'DashboardPaneContextType';
	var CATEGORY1 = 'Category1';
	var CATEGORY2 = 'Category2';
	var COLLECTION = 'Collection';
	var FOOD = "Food";
	var FORMULATED_MATERIAL = "FormulatedMaterial";
	var FORMULATED_PRODUCT = "FormulatedProduct";
	var LIB_MATERIAL = 'LibMaterial';
	var MATERIAL = 'Material';
	var MSG = 'MaterialSecurityGroup';
	var PANE = 'DashboardPane';
	var PROD_HIERARCHY = 'ProductHierarchy';
	var PROOFING = 'Proofing';
	var PROJECT = 'Project';
	var SEASON = "Season";
	var STYLE = "Style";
	var USER = "User";

	var CONTEXT_TYPES = {
		[FORMULATED_PRODUCT]: [PROD_HIERARCHY],
		[MATERIAL]: [MSG, LIB_MATERIAL],
		[STYLE]: [SEASON, CATEGORY1, CATEGORY2, COLLECTION],
		[PROJECT]: [PROJECT]
	};

	var DOMAIN_TYPES = {
		[LIB_MATERIAL]: MATERIAL,
		[MSG]: MATERIAL,
		[SEASON]: STYLE,
		[CATEGORY1]: STYLE,
		[CATEGORY2]: STYLE,
		[COLLECTION]: STYLE,
		[USER]: '',
		[PROD_HIERARCHY]: FORMULATED_PRODUCT,
		[PROOFING]: '',
		[PROJECT]: PROJECT
	};

	return declare(FormWidget, {
		'class': 'csi-form-view-block csi-homepage-form',

		/** @property {string[]} allowedPaneIds - explicit list of allowed panes to add to view */
		allowedPaneIds: [],

		/**
		 * Return context types of views that are allowed for selection in this form
		 * @return {string[]}
		 */
		getContextTypes: function() {
			return CONTEXT_TYPES[this.getDomainType()];
		},

		/**
		 * Return current domain type for form. On Style/Material forms this is constant, but on Sourcing form it is dynamic.
		 * @return {string}
		 */
		getDomainType: function() {
			return this.defView.getSource().actionValue;
		},

		/**
		 * Return domain type associated with selected context type.
		 * @param {string} contextType
		 * @return {string}
		 */
		getDomainTypeForContext: function(contextType) {
			return DOMAIN_TYPES[contextType];
		},

		/**
		 * Return store for fields.
		 * @param {Field} field
		 * @param {Any} value
		 * @return {Memory} store
		 */
		getFieldStore: function(field, value) {
			const name = field.getControlName();
			let store;

			if (name == CONTEXT) {
				const contextType = this.getFieldValue(CONTEXT_TYPE);
				if (contextType) {
					const paneId = this.getFieldValue(PANE);

					if (contextType == HomePageUtil.GLOBAL_CONTEXT) {
						// return enum store for State attribute
						const rc = this.getSourceView().getPaneClass(paneId);
						store = rc ? HomePageUtil.getStateStore(rc) : null;

						if (store) {
							field.setType(xml.ATTR_ENUM, store.enumId);
						}
					} else {
						// return store by querying for all nodes of context type
						if ([SEASON, CATEGORY1, CATEGORY2, COLLECTION].includes(contextType)) {
							store = HomePageUtil.getContextValuesStore(paneId, contextType);
							field.setType(xml.ATTR_STRING);
						} else if (contextType == PROD_HIERARCHY && (paneId.includes("Food") || paneId.includes("FormulatedMaterial"))) {
							const ctx = this.getContext();
							const formulatedProductType = (paneId.includes("FormulatedMaterial") ? FORMULATED_MATERIAL : FOOD);
							const query = new Query().Attr("ApplicableProductTypes", xml.OP_EQUALS, "ProductType:" + formulatedProductType, "Child:Subtype/Child:Settings");

							const rel = lang.mixin(new Relationship(), {
								m_typeNode: contextType,
								m_query: query // filter by the subtype
							});

							store = ctx.view.createHeadingNamesStore(ctx, field, null, rel);

							field.setType(xml.ATTR_REF);
						} else {
							// Special treatment for season and material context type
							// to avoid problems with migration.
							store = this.createHeadingNamesStore(this.getContext(), field, null, null, contextType, {
								request: csiStore.searchByType(contextType).getRequest(),
								typeNode: contextType
							});
							field.setType(xml.ATTR_REF);
						}
					}
				}
			}

			return store || this.inherited(arguments);
		},

		/**
		 * Return true if creating pane with inspiration supplier requests or quotes
		 * @return {boolean}
		 */
		isInspirationPane() {
			return HomePageUtil.isInspirationPane(this.getFieldValue(PANE));
		},

		/**
		 * Set the contents of the context dropdown when user
		 * selects a value from the the context type dropdown.
		 * @param {Field} field
		 */
		onChangeField: function(field) {
			this.inherited(arguments);

			if (field.m_name == CONTEXT_TYPE || field.m_name == PANE) {
				this._updateContext();
			}
		},

		/**
		 * Update available context types when pane changes.
		 * @param {View} defView
		 * @param {Field} field
		 * @param {Any} value
		 */
		onChangePane: function(defView, field, value) {
			this._updateContextTypes();
		},

		/**
		 * Return store indicating State attribute is context.
		 * @return {Memory}
		 */
		_getContextTypeStateStore: function() {
			var id = this.getFieldValue(PANE);
			var rc = this.getSourceView().getPaneClass(id);

			return new Memory({
				labelProperty: 'name',
				data: [{id: HomePageUtil.GLOBAL_CONTEXT, name: HomePageUtil.getStateName(rc)}]
			});
		},

		/**
		 * Return context types available for selected pane.
		 * @return {string[]}
		 */
		_getPaneContextTypes: function() {
			var paneControl = this.getFieldControl(PANE);
			var paneStore = paneControl.get('store');
			var paneValue = paneControl.get('value');
			var paneItem = paneStore.get(paneValue);
			return paneItem && paneItem.types;
		},

		/**
		 * Return context types available for selected pane.
		 * @param {string[]} [types] - optional list of context types
		 * @return {Memory}
		 */
		_getTypeStore: function(types) {
			var data = _.map(types, function(type) {
				return {id: type, name: locale.type(type)};
			});
			// add unselected value
			data.unshift({name: "", id: ""});

			// Don't sort, so they'll be in the order we specified
			// when we created the data, hierarchy order.
			return new Memory({
				labelProperty: 'name',
				data
			});
		},

		/**
		 * Return value to use with new store given the previous value. If the previous value is still in the store,
		 * then return it. If the store has only one entry, then return it. Otherwise return an empty value.
		 * @param {Memory} store
		 * @param {string} value
		 * @return {string}
		 */
		_getTypeStoreValue: function(store, value) {
			var data = store.query();
			var item;

			if (data.length == 1) { // if only 1 choice
				return store.getIdentity(data[0]);
			} else if (data.length == 2 && store.getIdentity(data[0]) == "") { // only 1 choice plus a blank
				return store.getIdentity(data[1]);
			} else if (store.get(value)) {
				return value;
			}

			return '';
		},

		/**
		 * Setup view parameters.
		 */
		_resolveView: function() {
			var contextTypes = this.getContextTypes();
			var field;

			// setup DashboardPane field
			field = this.defView.getField(PANE);
			if (field) {
				field.widgetParams = {store: this.getSourceView().getHomeViewRefStore(this.allowedPaneIds, contextTypes)};
			}

			if (contextTypes) {
				// setup DashboardPaneContextType field
				field = this.defView.getField(CONTEXT_TYPE);
				if (field) {
					field.widgetParams = {store: this._getTypeStore(contextTypes)};
				}
			}

			// setup DashboardPaneContext field
			field = this.defView.getField(CONTEXT);
			if (field) {
				field.bCombineWithPrevious = true;
			}

			this.inherited(arguments);
		},

		/**
		 * Make sure available states match selected pane.
		 */
		_updateContextWithState: function() {
			var contextField = this.getField(CONTEXT);
			if (!contextField) {
				return; // if context doesn't apply
			}

			var contextControl = this.getFieldControl(contextField);
			var contextStore = this.getFieldStore(contextField);
			var contextValue = this._getTypeStoreValue(contextStore, this.getFieldValue(contextField));

			contextControl.set('store', contextStore);
			contextControl.set('value', contextValue);
		},

		/**
		 * Update available context types.
		 */
		_updateContextTypes: function() {
			var domainType = this.getDomainType();
			var paneTypes = this._getPaneContextTypes();
			var typeField = this.getField(CONTEXT_TYPE);
			var typeControl = this.getFieldControl(typeField);
			var contextTypeOld = this.getFieldValue(typeField);
			var contextTypeStore;
			var contextType;

			if (domainType) {
				// convert context types for pane to domain types
				var contextTypes = {};

				_.each(paneTypes, function(contextType) {
					if (domainType == this.getDomainTypeForContext(contextType)) {
						contextTypes[contextType] = 1;
					}
				}, this);

				contextTypeStore = this._getTypeStore(_.keys(contextTypes));
				contextType = this._getTypeStoreValue(contextTypeStore, contextTypeOld);
			} else if (paneTypes && (paneTypes.length == 1) && !this.getDomainTypeForContext(paneTypes[0])) {
				// in this case, we are dealing with a global sourcing view, so set context type field to localized attribute name for State
				contextType = HomePageUtil.GLOBAL_CONTEXT;
				contextTypeStore = this._getContextTypeStateStore();
			} else {
				// assign empty store when no domain selected
				contextTypeStore = this._getTypeStore();
				contextType = '';
			}

			if (typeControl) {
				typeControl.set('store', contextTypeStore);
				typeControl.set('value', contextType);
			}

			// disable context type when there is only one choice
			html.setDisabled(typeControl, contextTypeStore.query().length == 1);

			// finally, update context store if pane is set to a sourcing view with enum store - this will happen automatically in most cases when
			// the context type changes, but if it doesn't (switch Pane from Factory to PO), then need to make sure correct enum values are shown
			if ((contextType == HomePageUtil.GLOBAL_CONTEXT) && (contextTypeOld == HomePageUtil.GLOBAL_CONTEXT)) {
				this._updateContext();
			}
		},

		/**
		 * Update state store for context type to make sure label matches name for state attribute
		 */
		_updateContextTypeWithState: function() {
			const typeControl = this.getFieldControl(CONTEXT_TYPE);
			if (!typeControl) {
				return; // if context type doesn't apply
			}

			typeControl.set('store', this._getContextTypeStateStore());
			typeControl.set('value', HomePageUtil.GLOBAL_CONTEXT);
		},

		/**
		 * Make sure store and value are correct.
		 */
		_updateContext: function() {
			const contextField = this.getField(CONTEXT);
			if (!contextField) {
				return; // if context doesn't apply
			}

			const contextControl = this.getFieldControl(contextField);
			const contextStore = this.getFieldStore(contextField);
			const contextValue = this._getTypeStoreValue(contextStore, this.getFieldValue(contextField));

			contextControl.set('store', contextStore);
			contextControl.set('value', contextValue);
		}

	});
});
